// Layout del panel admin: sidebar fijo + outlet.
// Es independiente del Layout público (sin Nav editorial ni Footer).

import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard, Calendar, Users, Sparkles, Tag, ClipboardList, LogOut, UserCircle, Mail,
  Coins, Wallet, CalendarDays, Building2, Ticket, TrendingUp, UserMinus, Shield,
  PartyPopper, Instagram, Film, BadgePercent, Images, Type,
} from 'lucide-react';

import Logo from '../Logo';
import { useAuth } from '../../context/AuthContext';
import { tt } from '../../lib/empresasTxt';
import { st } from '../../lib/solicitudesTxt';

export default function AdminLayout() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const secciones = [
    {
      label: t('admin.nav.seccion.operacion'),
      items: [
        { to: '/admin',          icon: LayoutDashboard, label: t('admin.nav.dashboard'), end: true },
        { to: '/admin/reservas', icon: ClipboardList,   label: t('admin.nav.reservas') },
        { to: '/admin/horarios', icon: Calendar,        label: t('admin.nav.horarios') },
        { to: '/admin/sesiones', icon: CalendarDays,    label: t('admin.nav.sesiones') },
      ],
    },
    {
      label: t('admin.nav.seccion.catalogo'),
      items: [
        { to: '/admin/clases-tipos', icon: Sparkles,    label: t('admin.nav.clases_tipos') },
        { to: '/admin/bonos-tipos',  icon: Tag,         label: t('admin.nav.bonos_tipos') },
        { to: '/admin/eventos',      icon: PartyPopper, label: t('admin.nav.eventos') },
        { to: '/admin/videos',       icon: Film,        label: t('admin.nav.videos') },
        { to: '/admin/promociones',  icon: BadgePercent, label: t('admin.nav.promociones') },
        { to: '/admin/codigos',      icon: Tag, label: t('admin.nav.codigos') },
        { to: '/admin/tramos-empresa', icon: BadgePercent, label: t('admin.nav.tramos_empresa') },
        { to: '/admin/pedidos-empresa', icon: Tag, label: tt(i18n.resolvedLanguage || 'es', 'a_titulo') },
        { to: '/admin/solicitudes-clase', icon: Tag, label: st(i18n.resolvedLanguage || 'es', 'a_titulo') },
      ],
    },
    {
      label: t('admin.nav.seccion.web'),
      items: [
        { to: '/admin/portada', icon: Type,   label: t('admin.nav.portada') },
        { to: '/admin/galeria', icon: Images, label: t('admin.nav.galeria') },
      ],
    },
    {
      label: t('admin.nav.seccion.profesores'),
      items: [
        { to: '/admin/profesores',           icon: Users,  label: t('admin.nav.profesores') },
        { to: '/admin/profesor-tarifas',     icon: Coins,  label: t('admin.nav.profesor_tarifas') },
        { to: '/admin/pagos-profesor',       icon: Wallet, label: t('admin.nav.pagos_profesor') },
        { to: '/admin/configuracion-emails', icon: Mail,   label: t('admin.nav.config_emails') },
      ],
    },
    {
      label: t('admin.nav.seccion.alumnos'),
      items: [
        { to: '/admin/bonos-comprados',   icon: Ticket,     label: t('admin.nav.bonos_comprados') },
        { to: '/admin/alumnos-top',       icon: TrendingUp, label: t('admin.nav.alumnos_top') },
        { to: '/admin/alumnos-inactivos', icon: UserMinus,  label: t('admin.nav.alumnos_inactivos') },
      ],
    },
    {
      label: t('admin.nav.seccion.sistema'),
      items: [
        { to: '/admin/configuracion-estudio', icon: Building2,  label: t('admin.nav.config_estudio') },
        { to: '/admin/redes',                 icon: Instagram,  label: t('admin.nav.redes') },
        ...(user?.es_super_admin ? [
          { to: '/admin/usuarios-admin', icon: Shield, label: t('admin.nav.usuarios_admin') },
        ] : []),
        { to: '/admin/mi-cuenta', icon: UserCircle, label: t('admin.nav.mi_cuenta') },
      ],
    },
  ];

  return (
    <div className="admin-app">
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <Logo />
          <span className="admin-brand-tag">admin</span>
        </div>
        <nav className="admin-nav">
          {secciones.map((sec, i) => (
            <div key={i} className="admin-nav-section">
              <div className="admin-nav-section-title">{sec.label}</div>
              {sec.items.map(({ to, icon: Icon, label, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  className={({ isActive }) => 'admin-nav-link' + (isActive ? ' active' : '')}
                >
                  <Icon size={18} strokeWidth={1.5} />
                  <span>{label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="admin-user">
          <div className="admin-user-info">
            <span className="admin-user-name">{user?.nombre || user?.email}</span>
            <span className="admin-user-rol">{user?.rol}</span>
          </div>
          <button type="button" className="admin-user-logout" onClick={handleLogout} aria-label={t('auth.menu.cerrar_sesion')}>
            <LogOut size={16} strokeWidth={1.5} />
          </button>
        </div>
      </aside>
      <main className="admin-main">
        <Outlet />
      </main>
    </div>
  );
}
